import { Heading, ScrollArea, Text } from '@radix-ui/themes';
import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { useState } from 'react';
import EvaluationIcons from 'components/HubertUI/EvaluationStatus/EvaluationIcons';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { useGetRecordingMediaUrlQuery } from 'rtk/query/apis/scores';
import type {
  TInterviewSummary,
  TInterviewSummaryBlock,
  TInterviewSummaryBlockDetail,
} from 'types/interviewSummary.types';

/**
 * Player for a recording answer's durable media link in the summary. The link's token (last
 * path segment) is exchanged backend-side for a fresh signed URL; bytes stream from GCS.
 * Click-to-load so opening a summary doesn't mint URLs for every recording row.
 */
const SummaryRecordingPlayer = ({ media }: { media: NonNullable<TInterviewSummaryBlockDetail['media']> }) => {
  const [load, setLoad] = useState(false);
  const token = media.link.split('/').pop() ?? '';
  const { data, isFetching, isError } = useGetRecordingMediaUrlQuery(token, { skip: !load || !token });

  if (!token) return null;
  if (!load) {
    return (
      <button type="button" className="text-primary self-start text-sm underline" onClick={() => setLoad(true)}>
        {media.kind === 'audio' ? '▶ Play audio answer' : '▶ Play video answer'}
      </button>
    );
  }
  if (isFetching) {
    return (
      <Text size="1" color="gray">
        Loading recording…
      </Text>
    );
  }
  if (isError || !data?.url) {
    return (
      <Text size="1" color="red">
        Recording unavailable.
      </Text>
    );
  }
  return media.kind === 'audio' ? (
    <audio controls preload="metadata" src={data.url} className="w-full max-w-md" />
  ) : (
    <video controls preload="metadata" src={data.url} className="max-h-64 w-full max-w-md rounded" />
  );
};

const SummaryElementDetail = ({
  detail,
  detailIndex,
  ...props
}: {
  detail: TInterviewSummaryBlockDetail;
  detailIndex: number;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleClick = () => {
    setOpen(!open);
  };

  return (
    <TableRow
      {...props}
      className={clsx(
        'flex w-full flex-row',
        'border-0',
        detailIndex !== 0 && 'border-t border-t-gray-100',
        'last:border-t last:border-t-gray-100',
      )}
    >
      <TableCell
        className={clsx(
          'p-[8px]',
          'hubert-vertical-center',
          'align-middle',
          'flex-shrink-0',
          'w-[5%]',
          'min-w-[5%]',
          'max-w-[5%]',
        )}
      >
        <EvaluationIcons
          evaluation={{
            name: detail.icon,
            bonus_points: detail.bonuspoints,
            points: detail.points,
          }}
        />
      </TableCell>
      <TableCell
        className={clsx(
          'w-[35%]',
          'min-w-[35%]',
          'max-w-[35%]',
          'flex-shrink-0',
          'font-bold',
          'p-[8px]',
          'hubert-vertical-center',
          'align-middle',
          'text-wrap',
          'break-words',
          'overflow-hidden',
        )}
      >
        <Text className="text-wrap break-words">{detail.label}</Text>
      </TableCell>
      <TableCell
        className={clsx(
          'flex-grow',
          'min-w-0',
          'p-[8px]',
          'hubert-vertical-center',
          'align-middle',
          'border-l border-l-gray-100',
          'overflow-hidden',
        )}
        onClick={handleClick}
      >
        {detail.options && detail.options.length ? (
          <div className="flex flex-col justify-start gap-4">
            {detail.options.map((option, index) => {
              if (option.evaluation) {
                return (
                  <div key={index} className="flex flex-row justify-start gap-2">
                    <span className="" style={{ fontSize: '1rem' }}>
                      <EvaluationIcons evaluation={option.evaluation} />
                    </span>
                    {/* Echoed from the candidate payload — must never be rendered as HTML (XSS) */}
                    <span className="hubert-vertical-center">{option.label}</span>
                  </div>
                );
              } else {
                let variant = option.answered ? '' : 'outline-';
                variant += option.isCorrect ? 'success' : 'danger';
                return (
                  <HubertBadge
                    key={`summary-test-option-${index}`}
                    variant={option.isCorrect ? 'success' : 'danger'}
                    className={clsx(variant, 'm-2')}
                  >
                    {option.label}
                  </HubertBadge>
                );
              }
            })}
          </div>
        ) : (
          <div className={clsx('flex flex-col gap-2')}>
            {detail.media?.link && <SummaryRecordingPlayer media={detail.media} />}
            <div className={clsx('text-wrap', 'whitespace-pre-line')}>
              {detail.answer ? detail.answer.replace(/<br\s*\/?>/gi, '\n').trim() : ''}
            </div>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

const SummaryElement = ({ summaryElement, ...props }: { summaryElement: TInterviewSummaryBlock }) => {
  return (
    <Table {...props} className={clsx('border-collapse', 'w-full')}>
      <Heading>{summaryElement.header}</Heading>
      <TableBody className={clsx('border border-solid border-gray-100')}>
        {summaryElement.details.map((detail, detailIndex) => {
          return (
            <SummaryElementDetail
              key={`summary-element-detal-${detailIndex}`}
              detail={detail}
              detailIndex={detailIndex}
            />
          );
        })}
      </TableBody>
    </Table>
  );
};

interface Props extends ComponentProps<'div'> {
  summary: TInterviewSummary;
  isActive: boolean;
}

const Summary = ({ summary, isActive, ...props }: Props) => {
  if (!isActive) return null;

  return (
    <div {...props} className={clsx('summary')}>
      <ScrollArea type="hover" className={clsx('max-h-[calc(100vh-224px)]', 'h-full w-full', 'col-span-1', 'pr-4')}>
        {summary.summary.map((summaryElement, summaryElementIndex) => {
          return <SummaryElement key={`summary-element-${summaryElementIndex}`} summaryElement={summaryElement} />;
        })}
      </ScrollArea>
    </div>
  );
};

export default Summary;
